/**
 * Wetter — one card per aerodrome the plan names, in the order the flight uses
 * them, and the wind at altitude under them. `docs/design/aether-ux.md` §10.
 *
 * There is no button in this pane and there is nothing to press. The briefing
 * arrives on its own and re-reads itself every ten minutes; its raw text is
 * written into the form's Wetter-Beratung lines by the same pass; a neighbour's
 * observation is written into a free ATIS row by the same pass again. What is
 * left for a pane to do is say what the weather IS, how old it is, and — where
 * an aerodrome publishes nothing — what stands in for it and whose report that
 * is.
 *
 * Everything is read from a context or from the document, never fetched here.
 * That is also what makes the pane work on a shared read-only link, which never
 * fetches anything: the cards fall back to `plans.meta.wx` and the form's own
 * ATIS rows, and the flight category is derived back out of the decoded cells
 * (`model.ts`) rather than going missing.
 */
import { useEffect, useMemo, useState } from 'react';

import { parseHM, fmtHM } from '@avia/core';

import { EmptyState } from '../../app/EmptyState';
import { BriefingLinks } from './BriefingLink';
import { Freshness } from '../../components/Freshness';
import { parseDateParts } from '../../fields';
import { useT } from '../../i18n';
import { usePlanRev, usePlanStore, useWxStatus } from '../../planContext';
import { ATIS_ROWS, ICAO_RE, atisField } from '../../wx';
import { useWx } from '../../wxContext';
import { StationCard } from './StationCard';
import { WindsAloft } from './WindsAloft';
import { weatherCards, type AtisCells, type CardRole } from './model';

import './weather.css';

/** A relative age has to keep up with the clock; half a minute is close enough. */
const TICK_MS = 30_000;

/** One choice for the whole app, remembered: plain text first, raw behind it. */
const LS_RAW = 'aether.wx.raw';

const readRaw = (): boolean => {
  try {
    return localStorage.getItem(LS_RAW) === '1';
  } catch {
    return false;
  }
};

/** The three cells a briefing is about, in flight order. */
const ROLES: readonly { role: CardRole; field: string }[] = [
  { role: 'von', field: 'von' },
  { role: 'nach', field: 'nach' },
  { role: 'alt', field: 'alt_name' },
];

/** Epoch ms of a "HH:MM" on the plan's date, or null when either is missing. */
function planMs(datum: string, hm: string): number | null {
  const p = parseDateParts(datum);
  const mins = parseHM(hm);
  if (!p || !Number.isFinite(mins)) return null;
  const ms = Date.UTC(p.y, p.mo - 1, p.d) + mins * 60_000;
  return Number.isFinite(ms) ? ms : null;
}

export function WeatherPane() {
  const t = useT();
  const store = usePlanStore();
  const rev = usePlanRev();
  const wx = useWx();
  const status = useWxStatus();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const [raw, setRaw] = useState(readRaw);
  useEffect(() => {
    try {
      localStorage.setItem(LS_RAW, raw ? '1' : '0');
    } catch {
      /* private mode: the choice simply does not survive the tab */
    }
  }, [raw]);

  /* The form's two ATIS rows: the only decoded weather a stored plan carries,
     and therefore what a shared link and a tab before the first fetch read. */
  const atis = useMemo<AtisCells[]>(
    () =>
      ATIS_ROWS.map(row => ({
        row,
        station: store.val(atisField(row, 'station')),
        zeit: store.val(atisField(row, 'zeit')),
        wind: store.val(atisField(row, 'wind')),
        sicht: store.val(atisField(row, 'sicht')),
        wolken: store.val(atisField(row, 'wolken')),
        temp: store.val(atisField(row, 'temp')),
        qnh: store.val(atisField(row, 'qnh')),
        tendenz: store.val(atisField(row, 'tendenz')),
      })),
    [store, rev],
  );

  const stations = useMemo(() => {
    const out: { role: CardRole; id: string }[] = [];
    for (const { role, field } of ROLES) {
      const id = store.val(field).trim().toUpperCase();
      if (!ICAO_RE.test(id) || out.some(x => x.id === id)) continue;
      out.push({ role, id });
    }
    return out;
  }, [store, rev]);

  const cards = useMemo(
    () =>
      weatherCards({
        stations,
        parsed: wx?.load?.parsed ?? [],
        nearby: wx?.nearby ?? [],
        status,
        atis,
        names: Object.fromEntries(
          (wx?.nearby ?? []).filter(n => n.name).map(n => [n.id, n.name] as const),
        ),
      }),
    [stations, wx?.load, wx?.nearby, status, atis],
  );

  /* The flight's own two times on the TAF band. Only where the plan carries a
     date: a tick drawn on a guessed day is worse than no tick. */
  const marks = useMemo(() => {
    const datum = store.val('datum');
    const etd = planMs(datum, store.val('etd') || store.val('atd'));
    let eta = planMs(datum, store.val('eta'));
    if (etd !== null && eta !== null && eta < etd) eta += 86_400_000;
    return { etd, eta };
  }, [store, rev]);

  const sun = useMemo(() => {
    const eta = parseHM(store.val('eta'));
    const ss = parseHM(store.val('ss'));
    if (!Number.isFinite(eta) || !Number.isFinite(ss)) return null;
    const left = ss - eta;
    return { eta: store.val('eta'), ss: store.val('ss'), left };
  }, [store, rev]);

  const anyRaw = cards.some(c => c.metarRaw || c.tafRaw || c.nearby?.metarRaw || c.nearby?.tafRaw);
  const noRoom = wx?.load?.noRoom ?? [];

  return (
    /* `.ae-pane` already pads this tab (it is not `bare`), so the pane brings
       its own rhythm and nothing else */
    <div className="wx-pane">
      <div className="ae-pane-head">
        <h2 className="ae-eyebrow">{t('wx.pane.title')}</h2>
        <Freshness family="wx" label={false} />
      </div>

      {cards.length === 0 ? (
        <EmptyState title={t('wx.empty.title')} line={t('wx.empty.line')} mark={false} />
      ) : (
        <>
          {cards.map(c => (
            <StationCard
              key={c.role + c.id}
              card={c}
              now={now}
              raw={raw}
              busy={wx?.busy === true}
              etd={marks.etd}
              eta={marks.eta}
            />
          ))}

          {wx?.nearbyBusy ? <p className="wx-note">{t('wx.nearby.busy')}</p> : null}
          {noRoom.length ? <p className="wx-note">{t('wx.noroom', { ids: noRoom.join(', ') })}</p> : null}

          {anyRaw ? (
            <button
              type="button"
              className="ae-btn wx-rawtoggle"
              aria-pressed={raw}
              onClick={() => setRaw(v => !v)}
            >
              {t('wx.raw')}
            </button>
          ) : null}

          {sun ? (
            <p className="wx-sun">
              <span className="ae-eyebrow">{t('wx.sun.title')}</span>
              <span className="ae-num">{t('wx.sun.eta', { time: sun.eta + 'Z' })}</span>
              <span className="ae-num">{t('wx.sun.ss', { time: sun.ss + 'Z' })}</span>
              <span className={sun.left < 0 ? 'wx-sun-after' : 'wx-sun-left'}>
                {sun.left < 0
                  ? t('wx.sun.after', { span: fmtHM(-sun.left) })
                  : t('wx.sun.left', { span: fmtHM(sun.left) })}
              </span>
            </p>
          ) : null}
        </>
      )}

      <WindsAloft />

      <BriefingLinks icaos={stations.map(s => s.id)} />
      <p className="wx-safety">{t('form.wx.note')}</p>
    </div>
  );
}
